import { useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  HomeIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { interpolate } from '../gallery/format';
import {
  ancestorPaths,
  collectParentPaths,
  filterFolderNodes,
  isNodeVisible,
  type FolderNode,
} from './folder-tree';

/**
 * Sélecteur de dossier REPLIABLE et recherchable (module `organize`).
 *
 * Rendu HIÉRARCHIQUE : chaque dossier ayant des enfants porte un chevron cliquable
 * (`aria-expanded`) ; l'arbre est REPLIÉ par défaut (seuls la racine et ses enfants
 * de 1er niveau sont visibles), sauf les ancêtres du dossier initialement sélectionné,
 * dépliés pour le révéler. Une RECHERCHE active bascule sur une liste filtrée PLATE
 * (tous les niveaux, sans chevrons) — la hiérarchie reste lisible par l'indentation.
 *
 * La racine (`''`) est toujours proposée en tête comme cible sélectionnable. La
 * sélection est signalée par une coche (icône) + `aria-pressed` (jamais la couleur
 * seule). Composant réutilisable : toutes les chaînes visibles arrivent via `labels`.
 */
export interface FolderTreePickerLabels {
  /** Placeholder + libellé du champ de recherche. */
  search: string;
  /** Message pendant le chargement des dossiers. */
  loading: string;
  /** Message d'échec de chargement des dossiers. */
  error: string;
  /** Libellé de la racine (`folders.root`). */
  root: string;
  /** Gabarit `{name}` — titre/aria-label du bouton de sélection d'un dossier. */
  selectFolder: string;
  /** Gabarit `{count}` — nombre de fichiers directs d'un dossier. */
  folderCount: string;
  /** Gabarit `{name}` — aria-label du chevron pour DÉPLIER un dossier. */
  expand: string;
  /** Gabarit `{name}` — aria-label du chevron pour REPLIER un dossier. */
  collapse: string;
  /**
   * Message quand une RECHERCHE de dossier ne ramène rien (audit F10). Sans lui, la
   * liste rendait un `<ul>` VIDE ET MUET — l'utilisateur ne savait pas si sa recherche
   * n'avait pas de résultat ou si le composant était cassé. Impacte Upload ET Déplacer.
   */
  searchEmpty: string;
}

interface FolderTreePickerProps {
  /** État de chargement de la liste des dossiers. */
  status: 'loading' | 'error' | 'ready';
  /** Arbre complet TRIÉ (racine + ancêtres recomposés) — `buildFolderTree(...)`. */
  nodes: FolderNode[];
  /** Chemin sélectionné (`''` = racine), ou `null` si aucun. */
  selected: string | null;
  /** Pose le chemin cliqué comme sélection. */
  onSelect: (path: string) => void;
  /** Verrou pendant un appel réseau (désactive les contrôles). */
  busy: boolean;
  /** Fenêtre agrandie → zone d'arbre plus haute. */
  enlarged: boolean;
  labels: FolderTreePickerLabels;
}

/** Dernier segment d'un chemin POSIX (nom affiché dans l'arbre). */
function lastSegment(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? path : path.slice(idx + 1);
}

export function FolderTreePicker(props: FolderTreePickerProps): React.JSX.Element {
  const { locale } = useI18n();
  const { status, nodes, selected, onSelect, busy, enlarged, labels } = props;

  const [search, setSearch] = useState('');
  // Déplié par défaut UNIQUEMENT sur les ancêtres du dossier initialement sélectionné
  // (pour le révéler à l'ouverture) ; sinon tout replié.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(ancestorPaths(selected ?? '')),
  );

  const searching = search.trim() !== '';
  const parentPaths = useMemo(() => collectParentPaths(nodes), [nodes]);
  const nonRoot = useMemo(() => nodes.filter((n) => n.path !== ''), [nodes]);
  const rows = useMemo(() => {
    if (searching) return filterFolderNodes(nonRoot, search);
    return nonRoot.filter((n) => isNodeVisible(n.path, expanded));
  }, [searching, nonRoot, search, expanded]);

  const toggle = (path: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const rootSelected = selected === '';
  const listMaxHeight = enlarged ? 'max-h-96' : 'max-h-56';

  return (
    <div className="flex flex-col gap-2">
      {/* Champ de recherche. */}
      <div className="relative">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={busy || status !== 'ready'}
          autoComplete="off"
          spellCheck={false}
          aria-label={labels.search}
          placeholder={labels.search}
          className="h-11 w-full rounded-md border border-border-strong bg-surface pl-10 pr-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
        />
      </div>

      {status === 'loading' && (
        <p role="status" className="inline-flex items-center gap-2 p-2 text-sm text-muted">
          <ArrowPathIcon
            className="h-5 w-5 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          {labels.loading}
        </p>
      )}
      {status === 'error' && (
        <p role="alert" className="inline-flex items-center gap-1 p-2 text-sm text-danger">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {labels.error}
        </p>
      )}

      {/* Recherche sans résultat : état VIDE explicite (jamais une liste muette). */}
      {status === 'ready' && searching && rows.length === 0 && (
        <p role="status" className="rounded-md border border-border p-3 text-sm text-muted">
          {labels.searchEmpty}
        </p>
      )}

      {status === 'ready' && !(searching && rows.length === 0) && (
        <ul className={`${listMaxHeight} overflow-y-auto rounded-md border border-border`}>
          {/* Racine — toujours proposée (cible légitime), sans chevron ni indentation. */}
          {!searching && (
            <li>
              <button
                type="button"
                onClick={() => onSelect('')}
                disabled={busy}
                aria-pressed={rootSelected}
                title={interpolate(labels.selectFolder, { name: labels.root })}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 ${
                  rootSelected ? 'bg-brand/10 text-brand' : 'text-fg hover:bg-surface-2'
                }`}
              >
                {rootSelected ? (
                  <CheckIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                ) : (
                  <HomeIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 truncate">{labels.root}</span>
              </button>
            </li>
          )}

          {rows.map((node) => {
            const isSelected = selected === node.path;
            const hasChildren = !searching && parentPaths.has(node.path);
            const isExpanded = expanded.has(node.path);
            // En recherche : chemin complet (contexte du match). En arbre : segment seul.
            const label = searching ? node.path : lastSegment(node.path);
            return (
              <li key={node.path}>
                <div
                  className="flex items-center"
                  style={{ paddingLeft: `${(node.depth - 1) * 1.25}rem` }}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => toggle(node.path)}
                      disabled={busy}
                      aria-expanded={isExpanded}
                      title={interpolate(isExpanded ? labels.collapse : labels.expand, {
                        name: node.path,
                      })}
                      aria-label={interpolate(isExpanded ? labels.collapse : labels.expand, {
                        name: node.path,
                      })}
                      className="shrink-0 rounded p-1 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    <span className="w-7 shrink-0" aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    onClick={() => onSelect(node.path)}
                    disabled={busy}
                    aria-pressed={isSelected}
                    title={interpolate(labels.selectFolder, { name: node.path })}
                    aria-label={interpolate(labels.selectFolder, { name: node.path })}
                    className={`flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-2 text-left text-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 ${
                      isSelected ? 'bg-brand/10 text-brand' : 'text-fg hover:bg-surface-2'
                    }`}
                  >
                    {isSelected ? (
                      <CheckIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                    ) : (
                      <FolderIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {node.assetCount !== undefined && (
                      <span className="shrink-0 text-xs text-muted">
                        {interpolate(labels.folderCount, { count: node.assetCount }, locale)}
                      </span>
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
